import { getEnv } from "../config/env";
import { getRecommendedThreshold, resolveQueryThreshold } from "../utils";

// Fallback cosine distances for package terms, from the threshold sheet.
const PACKAGE_EXCLUDE_THRESHOLD = getRecommendedThreshold(
	"PACKAGES",
	"bestMatch",
);

// services/PackageApiService.js - Package API Service

class PackageApiService {
	constructor() {
		const env = getEnv();
		this.baseURL = env.REACT_APP_API_URL;
		this.proxyUrl = env.REACT_APP_PROXY;
		this.timeout = 300000;
		this.filtersCache = new Map();
		this.filtersCacheTimeout = 10 * 60 * 1000;

		// Redirect prevention
		this.isRedirecting = false;
		this.lastRedirectTime = 0;
		this.REDIRECT_COOLDOWN = 5000; // 5 seconds cooldown

		console.log("📦 PackageApiService loaded with redirect loop prevention");
	}

	setProxyUrl(url) {
		this.proxyUrl = url.replace(/\/$/, "");
	}

	getSharedAuthToken() {
		if (
			window.apiService &&
			window.apiService.tokens &&
			window.apiService.tokens.idToken
		) {
			return window.apiService.tokens.idToken;
		}
		return (
			localStorage.getItem("google_id_token") ||
			localStorage.getItem("authToken")
		);
	}

	// Authentication check with redirect prevention
	isAuthenticated() {
		// Skip auth check if we're already redirecting
		if (this.isRedirecting) {
			console.log("🔄 Skipping auth check - redirect in progress");
			return false;
		}

		if (
			window.apiService &&
			typeof window.apiService.isAuthenticated === "function"
		) {
			try {
				const mainServiceAuth = window.apiService.isAuthenticated();
				if (!mainServiceAuth) {
					console.log(
						"⚠️ Main service auth failed, but not redirecting from package service",
					);
					return false; // Don't redirect here, let main service handle it
				}
				return mainServiceAuth;
			} catch (error) {
				console.warn("Main ApiService auth check failed:", error);
				return false; // Don't redirect on error
			}
		}

		const token = this.getSharedAuthToken();
		if (!token) {
			console.log("⚠️ No token found, but not redirecting from package service");
			return false; // Don't redirect here
		}

		return this.checkTokenExpiration(token);
	}

	// Fixed redirect with cooldown prevention
	redirectToLogin() {
		// Prevent multiple redirects in short succession
		const now = Date.now();
		if (
			this.isRedirecting ||
			now - this.lastRedirectTime < this.REDIRECT_COOLDOWN
		) {
			console.log("🚫 Redirect prevented - cooldown active");
			return;
		}

		this.isRedirecting = true;
		this.lastRedirectTime = now;

		console.log("🔐 Package API: Initiating single redirect to auth/login...");
		this.clearExpiredTokens();

		// Only redirect if we're not already on login page
		if (
			typeof window !== "undefined" &&
			window.location.pathname !== "/auth/login"
		) {
			// Use a timeout to prevent immediate loops
			setTimeout(() => {
				if (!this.isRedirecting) return; // Check if redirect was cancelled

				try {
					window.location.href = "/auth/login";
				} catch (error) {
					console.error("Redirect failed:", error);
					this.isRedirecting = false;
				}
			}, 100);
		} else {
			this.isRedirecting = false;
		}
	}

	clearExpiredTokens() {
		try {
			localStorage.removeItem("google_id_token");
			localStorage.removeItem("authToken");
			localStorage.removeItem("refresh_token");
			console.log("Package API: Expired tokens cleared from storage");
		} catch (error) {
			console.warn("Failed to clear tokens:", error);
		}
	}

	// Fixed token expiration check
	checkTokenExpiration(token) {
		if (!token) return false;

		try {
			const payload = this.parseJWT(token);
			if (!payload || !payload.exp) return false;

			const now = Date.now();
			const expiration = payload.exp * 1000;
			const isExpired = expiration <= now;

			if (isExpired) {
				console.log(
					"⚠️ Token expired, but not redirecting from package service",
				);
				// Don't redirect here - let the main app handle it
				return false;
			}

			return true;
		} catch (error) {
			console.warn("Token validation failed:", error);
			return false; // Don't redirect on validation error
		}
	}

	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	getUser() {
		// Don't redirect if not authenticated, just return null
		const token = this.getSharedAuthToken();
		if (!token) return null;

		if (window.apiService && typeof window.apiService.getUser === "function") {
			try {
				return window.apiService.getUser();
			} catch (error) {
				console.warn("Main ApiService getUser failed:", error);
			}
		}

		try {
			const payload = this.parseJWT(token);
			return payload
				? {
						id: payload.sub,
						email: payload.email,
						name: payload.name,
						picture: payload.picture,
						email_verified: payload.email_verified,
					}
				: null;
		} catch {
			return null;
		}
	}

	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		const token = this.getSharedAuthToken();
		if (token && this.checkTokenExpiration(token)) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	// Use proxy URL only
	async makeRequest(endpoint, options = {}) {
		try {
			const token = this.getSharedAuthToken();
			if (!token || this.isRedirecting) {
				return {
					success: false,
					error: "Authentication required",
					status: 401,
					skipRetry: true,
				};
			}

			const headers = this.getHeaders();
			const normalizedEndpoint = endpoint.startsWith("/")
				? endpoint
				: `/${endpoint}`;
			const url = `${this.proxyUrl}${normalizedEndpoint}`;

			const requestOptions = {
				method: "GET",
				...options,
				headers: { ...headers, ...options.headers },
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
			};

			console.log(`📦 Package API Request: ${requestOptions.method} ${url}`);

			const response = await fetch(url, {
				...requestOptions,
				signal: AbortSignal.timeout(this.timeout),
			});

			console.log(
				`📦 Package API Response: ${response.status} ${response.statusText}`,
			);

			if (response.status === 401 || response.status === 403) {
				console.log(
					"API authentication failed, but not redirecting from package service",
				);
				return {
					success: false,
					error: "Authentication failed",
					status: response.status,
					skipRetry: true,
				};
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				console.error("Package API Error Response:", errorText);
				throw new Error(
					`HTTP ${response.status}: ${response.statusText}. ${errorText}`,
				);
			}

			let data;
			let responseText;
			try {
				responseText = await response.text();
				console.log("Raw response length:", responseText.length);

				// Handle empty response
				if (!responseText.trim()) {
					console.warn(
						"Server returned empty response, likely due to timeout or server error",
					);
					return {
						success: false,
						error:
							"Server returned empty response. Try reducing the number of filters or search terms.",
						status: response.status,
						skipRetry: true,
					};
				}

				data = JSON.parse(responseText);
			} catch (parseError) {
				console.error("JSON parse failed:", parseError.message);
				console.error("Response was:", responseText?.substring(0, 500));

				return {
					success: false,
					error: `Server returned invalid response format. Try with fewer filters.`,
					status: response.status,
					skipRetry: true,
				};
			}

			return {
				success: true,
				data: data,
				status: response.status,
				headers: response.headers,
			};
		} catch (error) {
			console.error("📦 Package API Request Error:", error);

			let errorMessage = error.message;
			if (
				error.name === "TypeError" &&
				error.message.includes("Failed to fetch")
			) {
				errorMessage = "Network error: Unable to connect to API";
			}

			return {
				success: false,
				error: errorMessage,
				status: error.status || 500,
				skipRetry: true,
			};
		}
	}

	// Get filter options for packages
	async getPackageFilterOptions(signal = null) {
		try {
			console.log("📦 Fetching package filter options from API endpoint...");

			const user = this.getUser();
			const cacheKey = user
				? `package_filter_options_${user.id}`
				: "package_filter_options_anonymous";

			// Check cache first
			const cached = this.filtersCache.get(cacheKey);
			if (cached && Date.now() - cached.timestamp < this.filtersCacheTimeout) {
				console.log("✅ Using cached package filter options");
				return {
					success: true,
					data: cached.data,
					cached: true,
				};
			}

			// Simple auth check without redirect
			const token = this.getSharedAuthToken();
			if (!token || this.isRedirecting) {
				console.log("⚠️ No authentication for filter options, returning error");
				return {
					success: false,
					error: "Authentication required for filter options",
					status: 401,
				};
			}

			try {
				console.log(
					"🌐 Calling package filter values API: /package/get-filter-values",
				);

				const response = await this.makeRequest("/package/get-filter-values", {
					method: "GET",
					signal,
				});

				if (response.success && response.data) {
					console.log(
						"✅ Package filter options fetched successfully from API",
					);

					// Extract the packageFilterValues from the response
					const filterData = response.data.packageFilterValues || response.data;

					// Handle the new hierarchical structure
					if (filterData.categoryMap) {
						console.log("📊 Hierarchical category structure detected");

						// You can also flatten it for backward compatibility if needed
						filterData.packageCategoryL1 = Object.keys(filterData.categoryMap);

						// Create a flat list of all L2 categories for fallback
						const allL2Categories = new Set();
						Object.values(filterData.categoryMap).forEach((l2Array) => {
							// Check if l2Array exists and is an array before iterating
							if (l2Array && Array.isArray(l2Array)) {
								l2Array.forEach((item) => {
									if (item !== "null" && item !== "Null" && item !== null) {
										allL2Categories.add(item);
									}
								});
							}
						});
						filterData.packageCategoryL2 = Array.from(allL2Categories);
					}

					// Cache the successful response
					this.filtersCache.set(cacheKey, {
						data: filterData,
						timestamp: Date.now(),
					});

					return {
						success: true,
						data: filterData,
						cached: false,
					};
				} else if (response.skipRetry) {
					// Don't retry on auth errors
					return {
						success: false,
						error: response.error || "Authentication failed",
						status: response.status || 401,
					};
				} else {
					throw new Error(
						response.error || "Failed to fetch package filter options",
					);
				}
			} catch (apiError) {
				console.warn(
					"⚠️ Failed to fetch package filter options from API:",
					apiError.message,
				);
				return {
					success: false,
					error:
						apiError.message ||
						"Failed to fetch package filter options from API",
				};
			}
		} catch (error) {
			console.error("❌ Get package filter options failed completely:", error);
			return {
				success: false,
				error: error.message || "Package filter options request failed",
			};
		}
	}

	// Search packages with filters and search terms
	async searchPackages(searchParams = {}) {
		const requestId = Date.now();
		try {
			console.log(
				`📦 [${requestId}] Searching packages with params:`,
				searchParams,
			);

			const {
				searchTerms = [],
				excludeTerms = [],
				filters = {},
				sortBy = null,
				view = null,
				startIndex = null,
				noOfResult = 100,
				noOfDays = null,
				signal,
			} = searchParams;

			// Build the search payload matching your API structure
			const searchPayload = {
				query: searchTerms.map((term) => ({
					query: term.query || term.text || term,
					threshold: resolveQueryThreshold(term, "PACKAGES"),
				})),
				excludeQuery: excludeTerms.map((term) => ({
					query: term.query || term.text || term,
					threshold: term.threshold ?? PACKAGE_EXCLUDE_THRESHOLD,
				})),
				filters: {
					packageVector: {
						// String/null fields
						revision: filters.revision || null,
						rateCardCpm: filters.rateCardCpm || null,
						discountedCpm: filters.discountedCpm || null,
						dealPrice: filters.dealPrice || null,
						dealStartDate: filters.dealStartDate || null,
						dealEndDate: filters.dealEndDate || null,

						// Array fields - ensure they're arrays
						dealName: Array.isArray(filters.dealName) ? filters.dealName : [],
						audienceType: Array.isArray(filters.audienceType)
							? filters.audienceType
							: [],
						primaryVertical: Array.isArray(filters.primaryVertical)
							? filters.primaryVertical
							: [],
						adPlacement: Array.isArray(filters.adPlacement)
							? filters.adPlacement
							: [],
						distributionChannel: Array.isArray(filters.distributionChannel)
							? filters.distributionChannel
							: [],
						deviceTypes: Array.isArray(filters.deviceTypes)
							? filters.deviceTypes
							: [],
						country: Array.isArray(filters.country) ? filters.country : [],
						packageCategoryL1: Array.isArray(filters.packageCategoryL1)
							? filters.packageCategoryL1
							: [],
						packageCategoryL2: Array.isArray(filters.packageCategoryL2)
							? filters.packageCategoryL2
							: [],
						packageCategoryL3: Array.isArray(filters.packageCategoryL3)
							? filters.packageCategoryL3
							: [],
						audienceProviderName: Array.isArray(filters.audienceProviderName)
							? filters.audienceProviderName
							: [],
						publisherAccountNk: Array.isArray(filters.publisherAccountNk)
							? filters.publisherAccountNk
							: [],
						audienceId: Array.isArray(filters.audienceId)
							? filters.audienceId
							: [],
						dealId: Array.isArray(filters.dealId) ? filters.dealId : [],
						dealSource: Array.isArray(filters.dealSource)
							? filters.dealSource
							: [],
						dealStatus: Array.isArray(filters.dealStatus)
							? filters.dealStatus
							: [],
						audienceName: Array.isArray(filters.audienceName)
							? filters.audienceName
							: [],
						urlTargetingType: Array.isArray(filters.urlTargetingType)
							? filters.urlTargetingType
							: [],
						associatedAdvertisers: Array.isArray(filters.associatedAdvertisers)
							? filters.associatedAdvertisers
							: [],
						accountId: Array.isArray(filters.accountId)
							? filters.accountId
							: [],

						// Boolean fields
						isCurated: filters.isCurated || null,
						hasMeta: filters.hasMeta || null,
						excludeMfa: filters.excludeMfa || null,
					},
					packageMetrics: {
						// All numeric fields from your payload structure
						impressions: filters.impressions || null,
						fills: filters.fills || null,
						nonZeroBids: filters.nonZeroBids || null,
						cprAllRequests: filters.cprAllRequests || null,
						cprMarketRequests: filters.cprMarketRequests || null,
						cprClicks: filters.cprClicks || null,
						impressionsWithClickMacro:
							filters.impressionsWithClickMacro || null,
						cprPrivateMarketImpressions:
							filters.cprPrivateMarketImpressions || null,
						completeFirstOccurrence: filters.completeFirstOccurrence || null,
						startFirstOccurrence: filters.startFirstOccurrence || null,
						viewables: filters.viewables || null,
						viewabilityMeasures: filters.viewabilityMeasures || null,
						dealVolume: filters.dealVolume || null,
						bidCpm: filters.bidCpm * 1000000 || null,
						bidGuidance: filters.bidGuidance || null,
						cprMarketDemandPartnerSpend:
							filters.cprMarketDemandPartnerSpend || null,
						cprMarketPublisherRevenue:
							filters.cprMarketPublisherRevenue || null,
						nonZeroBidsValue: filters.nonZeroBidsValue || null,
						bidSpend: filters.bidSpend || null,
						clickThroughRate: filters.clickThroughRate || null,
						revenuePerImpression: filters.revenuePerImpression || null,
						costPerImpression: filters.costPerImpression || null,
						vtr: filters.vtr || null,
						viewability: filters.viewability || null,
						oxWinRate: filters.oxWinRate || null,
						pubWinRate: filters.pubWinRate || null,
						totalSpendInUSD: filters.totalSpendInUSD || null,
					},
					cumulativeDataFilters: {
						cumulativeSetsFiltersMap: filters.cumulativeSetsFiltersMap || {},
					},
				},
				sortBy: sortBy,
				view: view,
				startIndex: startIndex,
				noOfResult: noOfResult,
				noOfDays: noOfDays,
			};

			console.log(`📦 [${requestId}] Search payload:`, {
				queryCount: searchPayload.query.length,
				excludeQueryCount: searchPayload.excludeQuery.length,
				hasFilters: Object.keys(searchPayload.filters.packageVector).some(
					(key) =>
						searchPayload.filters.packageVector[key] &&
						(Array.isArray(searchPayload.filters.packageVector[key])
							? searchPayload.filters.packageVector[key].length > 0
							: true),
				),
				sortBy: searchPayload.sortBy,
				noOfResult: searchPayload.noOfResult,
			});

			const response = await this.makeRequest("/package/search", {
				method: "POST",
				body: JSON.stringify(searchPayload),
				signal,
			});

			if (response.success) {
				console.log(`✅ [${requestId}] Package search completed successfully`);
				return response;
			} else {
				console.error(
					`❌ [${requestId}] Package search failed:`,
					response.error,
				);
				return response;
			}
		} catch (error) {
			console.error(`❌ [${requestId}] searchPackages failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	/**
	 * Direct API call - NO transformation (for LLM service)
	 * Just passes the payload directly to the backend
	 */
	async searchPackagesDirect(searchPayload, signal = null) {
		const requestId = Date.now();
		try {
			console.log(
				`📦 [${requestId}] Direct package search - NO transformation`,
			);
			console.log(
				`📤 [${requestId}] Payload sent to API:`,
				JSON.stringify(searchPayload, null, 2),
			);

			const response = await this.makeRequest("/package/search", {
				method: "POST",
				body: JSON.stringify(searchPayload),
				signal,
			});

			if (response.success) {
				console.log(`✅ [${requestId}] Package search completed successfully`);
				return response;
			} else {
				console.error(
					`❌ [${requestId}] Package search failed:`,
					response.error,
				);
				return response;
			}
		} catch (error) {
			console.error(`❌ [${requestId}] searchPackagesDirect failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	// Clear filter cache
	clearFilterCache() {
		this.filtersCache.clear();
		console.log("✅ Package filter cache cleared");
	}
}

const packageApiService = new PackageApiService();
window.packageApiService = packageApiService;

console.log("📦 PackageApiService loaded with redirect loop prevention");

export default packageApiService;
